import { onlineOrderingHoursOf, resolvePrepLeadMinutes, type ILocation } from '@order-up/models';
import { formatTime, type TimeFormatSettings } from '../../utils/timeFormatter';

/**
 * The hours the storefront takes orders in: the location's online ordering
 * hours, or its business hours when none are set (`onlineOrderingHoursOf`).
 */
function orderingHours(locationRecord: ILocation | null | undefined) {
    return locationRecord ? onlineOrderingHoursOf(locationRecord) : undefined;
}

/**
 * Scheduling Utilities
 * Industry-level ASAP and scheduling logic for restaurant orders
 */

export interface TimeSlot {
    value: string; // '8:00 PM' or 'ASAP'
    displayText: string; // 'Ready by 8:00 PM' or 'Ready by 8:15 PM (ASAP)'
    readyTime: Date; // Actual ready time
    isASAP: boolean;
}

export interface DateOption {
    label: string; // 'Today' | 'Tomorrow' | 'Fri'
    shortLabel: string; // 'Nov 8'
    date: Date;
    isAvailable: boolean; // Whether restaurant is open on this date
}

/**
 * The minutes a guest is told an ASAP order takes: the LIVE restaurant-wide
 * quote (`usePublicPrepTime`) when the caller has it, else the restaurant's
 * standard prep time. One number for the whole kitchen — never a website-only
 * setting (docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md §7).
 */
export function asapPrepMinutes(
    locationRecord: ILocation | null | undefined,
    liveMinutes?: number | null,
): number {
    return typeof liveMinutes === 'number' && Number.isFinite(liveMinutes) && liveMinutes > 0
        ? liveMinutes
        : resolvePrepLeadMinutes(locationRecord);
}

/**
 * Calculate ASAP ready time
 * @param locationRecord - Restaurant configuration
 * @param liveMinutes - the live quote from `usePublicPrepTime`, when loaded
 * @returns Date object for when order will be ready, or null if ASAP not available
 */
export function calculateASAPTime(
    locationRecord: ILocation | null | undefined,
    liveMinutes?: number | null,
): Date | null {
    const hours = orderingHours(locationRecord);
    if (!locationRecord || !hours) return null;

    const orderingConfig = locationRecord.websiteConfig?.ordering?.config;
    const prepTimeMinutes = asapPrepMinutes(locationRecord, liveMinutes);
    const enableAsapOrders = orderingConfig?.enableAsapOrders ?? true;

    if (!enableAsapOrders) return null;

    // Calculate ready time = current time + prep time
    const now = new Date();
    const readyTime = new Date(now.getTime() + prepTimeMinutes * 60000);

    // Check if restaurant is open now and will be open at ready time
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[now.getDay()];
    const dayHours = hours[dayName];

    if (!dayHours || dayHours.closed) return null;

    // Parse opening and closing times
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;

    // "ASAP means ASAP": it's available whenever the restaurant is OPEN, right up
    // to close. We deliberately DON'T apply the scheduled-slot closing buffer or
    // reject because the prep estimate (now + prep) crosses close — ASAP just
    // moves the timeline (the kitchen finishes the last orders). This also keeps
    // the client in lockstep with the server, which accepts ASAP whenever open;
    // previously the client blocked checkout in the closing-buffer window even
    // though the server would have taken the order. The closing buffer still
    // applies to SCHEDULED time slots (see generateTimeSlots / isDateAvailable).
    if (currentMinutes < openMinutes || currentMinutes >= closeMinutes) {
        return null;
    }

    return readyTime;
}

/**
 * Check if ASAP is available right now
 */
export function isASAPAvailable(locationRecord: ILocation | null | undefined): boolean {
    return calculateASAPTime(locationRecord) !== null;
}

/**
 * Format ASAP display text (showing prep time, not exact time).
 * The order's own promise is taken at placement from the same quote.
 * @returns Formatted string like 'ASAP (25 mins)'
 */
export function formatASAPDisplay(
    locationRecord: ILocation | null | undefined,
    liveMinutes?: number | null,
): string {
    return `ASAP (${asapPrepMinutes(locationRecord, liveMinutes)} mins)`;
}

/**
 * Check if restaurant is open on a specific date
 */
export function isLocationOpenOnDate(
    locationRecord: ILocation | null | undefined,
    date: Date
): boolean {
    const hours = orderingHours(locationRecord);
    if (!hours) return false;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    const dayHours = hours[dayName];

    return dayHours ? !dayHours.closed : false;
}

/**
 * Check if a date should be available for selection
 * - Restaurant must be open on that date
 * - If it's today, must have available time slots (not past closing)
 */
export function isDateAvailable(
    locationRecord: ILocation | null | undefined,
    date: Date
): boolean {
    if (!isLocationOpenOnDate(locationRecord, date)) {
        return false;
    }

    // For today, check if there are still available time slots
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const hours = orderingHours(locationRecord);
    if (isToday && locationRecord && hours) {
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[date.getDay()];
        const dayHours = hours[dayName];

        if (!dayHours || dayHours.closed) return false;

        const orderingConfig = locationRecord.websiteConfig?.ordering?.config;
        const minLeadTimeMinutes = orderingConfig?.minLeadTimeMinutes ?? 15;
        const closingBufferMinutes = orderingConfig?.closingBufferMinutes ?? 15;

        const [closeHour, closeMin] = dayHours.close.split(':').map(Number);
        const closeMinutes = closeHour * 60 + closeMin;
        const lastOrderMinutes = closeMinutes - closingBufferMinutes;

        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const earliestOrderMinutes = currentMinutes + minLeadTimeMinutes;

        // If earliest possible order time is past last order time, not available
        if (earliestOrderMinutes >= lastOrderMinutes) {
            return false;
        }
    }

    return true;
}

/**
 * Generate smart date options (only showing available dates)
 * @param locationRecord - Restaurant configuration
 * @param maxDays - Maximum days to show (default 7)
 * @returns Array of date options with availability status
 */
export function generateSmartDateOptions(
    locationRecord: ILocation | null | undefined,
    maxDays: number = 7
): DateOption[] {
    const options: DateOption[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day

    let daysAdded = 0;
    let daysChecked = 0;

    // Keep checking days until we have enough available days or hit a limit
    while (daysAdded < maxDays && daysChecked < maxDays * 2) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() + daysChecked);

        const isAvailable = isDateAvailable(locationRecord, checkDate);

        // Only add available dates (industry-standard: hide unavailable dates entirely)
        if (isAvailable) {
            let label: string;
            if (daysChecked === 0) {
                label = 'Today';
            } else if (daysChecked === 1) {
                label = 'Tomorrow';
            } else {
                label = checkDate.toLocaleDateString('en-US', { weekday: 'short' });
            }

            options.push({
                label,
                shortLabel: checkDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                date: checkDate,
                isAvailable: true // Always true since we only add available dates
            });

            daysAdded++;
        }

        daysChecked++;
    }

    return options;
}

/**
 * Generate time slots for a specific date
 * @param locationRecord - Restaurant configuration
 * @param date - Date to generate slots for
 * @param includeASAP - Whether to include ASAP option (only for today)
 * @param settings - Optional `Restaurant.settings` for clockFormat respect; defaults to 12h
 * @returns Array of time slot strings
 */
export function generateTimeSlots(
    locationRecord: ILocation | null | undefined,
    date: Date,
    includeASAP: boolean = false,
    settings?: TimeFormatSettings,
): string[] {
    const times: string[] = [];

    // Add ASAP only if requested and available
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (includeASAP && isToday && isASAPAvailable(locationRecord)) {
        times.push('ASAP');
    }

    const hours = orderingHours(locationRecord);
    if (!locationRecord || !hours) {
        return times;
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    const dayHours = hours[dayName];

    if (!dayHours || dayHours.closed) {
        return times;
    }

    // Get ordering configuration
    const orderingConfig = locationRecord.websiteConfig?.ordering?.config;
    const minLeadTimeMinutes = orderingConfig?.minLeadTimeMinutes ?? 15;
    const timeSlotIntervalMinutes = orderingConfig?.timeSlotIntervalMinutes ?? 15;
    const closingBufferMinutes = orderingConfig?.closingBufferMinutes ?? 15;

    // Parse opening and closing times
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    // Calculate start time (opening + min lead time)
    let startMinutes = openHour * 60 + openMin + minLeadTimeMinutes;

    // Calculate end time (closing - closing buffer)
    const endMinutes = closeHour * 60 + closeMin - closingBufferMinutes;

    // If it's today, start from current time + min lead time
    if (isToday) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes() + minLeadTimeMinutes;
        startMinutes = Math.max(startMinutes, currentMinutes);

        // Round up to next time slot interval
        startMinutes = Math.ceil(startMinutes / timeSlotIntervalMinutes) * timeSlotIntervalMinutes;
    }

    // Generate time slots
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += timeSlotIntervalMinutes) {
        const hour = Math.floor(minutes / 60);
        const min = minutes % 60;
        const date = new Date();
        date.setHours(hour, min, 0, 0);

        times.push(formatTime(date, settings));
    }

    return times;
}

/**
 * Validate and calculate final scheduled time at order placement
 * For ASAP: Calculates ready time based on current time + prep time
 * For scheduled: Validates time is still valid and adjusts if needed
 *
 * @param locationRecord - Restaurant configuration
 * @param scheduledTime - Selected scheduled time (undefined for ASAP)
 * @returns Object with validated time and any error message
 */
export function validateAndCalculateOrderTime(
    locationRecord: ILocation | null | undefined,
    scheduledTime?: Date
): { scheduledTime: Date | undefined; error?: string } {
    // ASAP order: Calculate actual ready time now
    if (!scheduledTime) {
        const asapTime = calculateASAPTime(locationRecord);
        if (!asapTime) {
            return {
                scheduledTime: undefined,
                error: 'Restaurant is currently closed. Please schedule for later.'
            };
        }
        return { scheduledTime: asapTime };
    }

    // Scheduled order: Validate it's still valid
    const now = new Date();
    const orderingConfig = locationRecord?.websiteConfig?.ordering?.config;
    const minLeadTimeMinutes = orderingConfig?.minLeadTimeMinutes ?? 15;

    // Check if scheduled time has passed
    if (scheduledTime <= now) {
        return {
            scheduledTime: undefined,
            error: 'Selected time has passed. Please choose a new time.'
        };
    }

    // Check if it meets minimum lead time
    const minutesUntilScheduled = (scheduledTime.getTime() - now.getTime()) / 60000;
    if (minutesUntilScheduled < minLeadTimeMinutes) {
        return {
            scheduledTime: undefined,
            error: `Orders must be placed at least ${minLeadTimeMinutes} minutes in advance. Please choose a later time.`
        };
    }

    // Scheduled time is still valid
    return { scheduledTime };
}

/**
 * Live order-time status — the single evaluator the UI uses to keep the
 * pickup/delivery time HONEST as the customer lingers.
 *
 * - ASAP: the ready estimate is `now + prepTime`, recomputed against the
 *   supplied `now` so the expectation tracks real time (and flips to
 *   `asap_unavailable` when the kitchen can no longer make it before close).
 * - Scheduled: flags a slot that has PASSED or no longer meets the minimum lead
 *   time, so the UI can warn + block checkout instead of letting a stale order
 *   through.
 */
export type OrderTimeStatus =
    | 'asap'
    | 'scheduled'
    | 'scheduled_passed'
    | 'scheduled_too_soon'
    | 'asap_unavailable';

export interface OrderTimeEvaluation {
    status: OrderTimeStatus;
    /** May the customer proceed to checkout with this time? */
    isValid: boolean;
    /** Short control label, e.g. "ASAP · ready ~3:45 PM" or "Today 8:00 PM". */
    label: string;
    /** Customer-facing warning when the time is no longer valid. */
    warning?: string;
    /** Estimated ready time for ASAP orders. */
    readyEstimate?: Date;
}

export function evaluateOrderTime(
    locationRecord: ILocation | null | undefined,
    scheduledTime: Date | undefined,
    now: Date,
    settings?: TimeFormatSettings,
): OrderTimeEvaluation {
    const cfg = locationRecord?.websiteConfig?.ordering?.config;
    const minLeadTimeMinutes = cfg?.minLeadTimeMinutes ?? 15;

    if (scheduledTime) {
        const sched = new Date(scheduledTime);

        if (sched.getTime() <= now.getTime()) {
            return {
                status: 'scheduled_passed',
                isValid: false,
                label: formatTime(sched, settings),
                warning: 'Your selected time has passed. Please choose a new time.',
            };
        }

        const minutesUntil = (sched.getTime() - now.getTime()) / 60000;
        if (minutesUntil < minLeadTimeMinutes) {
            return {
                status: 'scheduled_too_soon',
                isValid: false,
                label: formatTime(sched, settings),
                warning: `Orders need at least ${minLeadTimeMinutes} min notice. Please pick a later time.`,
            };
        }

        const sameDay = sched.toDateString() === now.toDateString();
        const dayLabel = sameDay
            ? 'Today'
            : sched.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return {
            status: 'scheduled',
            isValid: true,
            label: `${dayLabel} ${formatTime(sched, settings)}`,
        };
    }

    // ASAP — re-derive the ready estimate against the current clock.
    const asap = calculateASAPTime(locationRecord);
    if (!asap) {
        return {
            status: 'asap_unavailable',
            isValid: false,
            label: 'ASAP',
            warning: 'We’re not taking ASAP orders right now. Please schedule for later.',
        };
    }
    return {
        status: 'asap',
        isValid: true,
        label: `ASAP · ready ~${formatTime(asap, settings)}`,
        readyEstimate: asap,
    };
}

